import type { FC } from 'react';
import { useContext, useEffect } from 'react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import { ReferralType } from '@wix/da-bi/pkg/constants';
import { BiContextProvidedValue, BiData, DefaultBiEvent } from '@wix/da-bi/pkg/events';
import BuyCoreBrandedButton from '@wix/da-shared-react/pkg/Button/core/dsPresets/BuyCoreBrandedButton';
import CoreExclusiveFeature from '@wix/da-shared-react/pkg/indicators/CoreExclusiveFeature/CoreExclusiveFeature';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import { PapiUser } from '@wix/da-papi-types';
import { ShopItemType } from '../../../../../../types/shop';
import EmptyState from '../../../../EmptyState';
import EmptyStateSubtitleWithLearnMoreLink from '../EmptyStateSubtitleWithLearnMoreLink';
import { Url } from '@wix/da-url';

import s from './PremiumFoldersEmptyState.scss';

export interface Props {
  view: 'wide' | 'large' | 'small';
  user: PapiUser;
  isOwner: boolean;
  onCreateGallery: () => void;
  hasCoreMembership?: boolean;
}

export const PremiumFoldersEmptyState: FC<Props> = ({
  view,
  user,
  user: { username },
  isOwner,
  onCreateGallery,
  hasCoreMembership,
}) => {
  const { t } = useTranslation();
  const isMobile = useContext(MobileContext);

  useEffect(() => {
    if (
      typeof window !== 'undefined' &&
      isOwner &&
      window.location.search.includes('new_premium_folder')
    ) {
      window.history.replaceState(null, document.title, window.location.pathname);
      onCreateGallery();
    }
  }, [isOwner, onCreateGallery]);

  if (isOwner) {
    return hasCoreMembership ? (
      <EmptyState
        className={classnames(s['root'], s[view])}
        title={t('pages.shop.emptyState.core.premiumFolder.title')}
        titleClassName={s['title']}
        subtitle={
          <EmptyStateSubtitleWithLearnMoreLink
            shopItemType={ShopItemType.PREMIUM_FOLDERS}
            subtitle={
              isMobile
                ? t('pages.shop.emptyState.core.premiumFolder.subtitle.mobile')
                : t('pages.shop.emptyState.core.premiumFolder.subtitle')
            }
          />
        }
        subtitleClassName={s['subtitle']}
        illustration={<CoreExclusiveFeature withoutBuyCTA />}
        illustrationClassName={s['exclusive']}
        withButton={!isMobile}
        buttonLabel={t('pages.shop.emptyState.core.premiumFolder.cta')}
        onButtonClick={onCreateGallery}
        biData={BiData<DefaultBiEvent>({
          evid: 2,
          click_info: 'create_premium_gallery',
          sectionname: BiContextProvidedValue,
        })}
        view={'small'}
      />
    ) : (
      <EmptyState
        className={classnames(s['root'], s[view])}
        title={t('pages.shop.emptyState.nonCore.premiumFolder.title')}
        titleClassName={s['title']}
        subtitle={
          <EmptyStateSubtitleWithLearnMoreLink
            shopItemType={ShopItemType.PREMIUM_FOLDERS}
            subtitle={t(
              isMobile
                ? 'pages.shop.emptyState.nonCore.premiumFolder.subtitle.mobile'
                : 'pages.shop.emptyState.nonCore.premiumFolder.subtitle'
            )}
          />
        }
        subtitleClassName={s['subtitle']}
        illustration={<CoreExclusiveFeature withoutBuyCTA />}
        illustrationClassName={s['exclusive']}
        customButton={
          isMobile ? undefined : (
            <BuyCoreBrandedButton
              size="large"
              biData={
                {
                  referral_info: ReferralType.SHOP_PREMIUM_GALLERIES,
                } as any
              }
              withSaleSparkles
            >
              {t('widgets.tierContent.empty.cta.getCore')}
            </BuyCoreBrandedButton>
          )
        }
        view={'small'}
      />
    );
  }

  const handleOpenChat = () => {
    window.open(Url.userMessageLink(user.username), '_blank');
  };

  // empty state for visitor
  return (
    <EmptyState
      className={classnames(s['root'], s[view])}
      title={t('pages.shop.emptyState.premiumFolders.title.visitor', {
        username,
      })}
      subtitle={t('pages.shop.emptyState.premiumFolders.subtitle.visitor')}
      subtitleClassName={s['subtitle']}
      onButtonClick={handleOpenChat}
      buttonLabel={t('pages.shop.emptyState.openChatWithArtist')}
      withButton
    />
  );
};
PremiumFoldersEmptyState.displayName = 'PremiumFoldersEmptyState';

export default PremiumFoldersEmptyState;
